/**
 * Phase 13 PhotoStrip 3-Source Unification — single shared PhotoTile primitive.
 *
 * Renders all 4 PhotoTileState kinds (D-01) with the unified visual treatment:
 *  - D-05: pill-at-corner convention (no inline message text on the tile face)
 *  - D-06: tile geometry (w-20 h-20 + rounded-md + shadow-sm + bg-surface-variant placeholder)
 *  - D-08: brand-token tints (no hex literals; cross-platform parity with Android Theme.kt)
 *  - D-19: transition-shadow duration-200 microinteraction on landed-tile hover
 *
 * Surface separation (V-M2 mitigation 1):
 *   - PM containers (PhotoStrip.tsx) call this with state typed as `PmPhotoTileState`
 *     (Exclude<PhotoTileState, { kind: 'local-only' }> + Omit<broken, 'onRetry'>).
 *     PM cannot emit local-only or pass onRetry — the type system rejects it.
 *   - Worker containers (Android — Plan 13-03) call this with the unrestricted
 *     PhotoTileState. The local-only branch only fires from worker-surface callers.
 *
 * Click matrix (D-09 + D-10):
 *   - landed     → button (cursor-zoom-in) → onLandedClick(photoId) opens lightbox.
 *   - uploading  → div (cursor-default, aria-disabled) → no click affordance.
 *   - broken     → div (cursor-default, aria-disabled) → no click; tooltip carries reason+date.
 *   - local-only → div (no click handler in PM context; Worker container wires this).
 *
 * D-11 + Blocker #1 — `formattedDate` is constructed by the CONTAINER
 * (PhotoStrip.tsx) using Intl.DateTimeFormat with numberingSystem: 'latn'.
 * The tile threads it into intl.formatMessage via the {date} interpolation.
 * Empty-string fall-through is the documented null state for an in-flight
 * upload that has no uploaded_at yet (uploading kind on PM side never has
 * uploaded_at). Date.prototype.toLocaleString() is NOT used because it does
 * not accept the numberingSystem option (V-CP4 Latin-digit lock).
 *
 * Blocker #4 — broken-state pill consumes M3-prefixed brand tokens
 * (`bg-m3-error-container` = #FFDAD6 + `text-m3-on-error-container` = #410002)
 * for cross-platform parity with Android `MaterialTheme.colorScheme.errorContainer`
 * (Theme.kt:35 rebound to BrandTokens.colorM3ErrorContainer). The bare
 * `bg-error-container` token resolves to brand soft pink #fff1f2 which differs
 * from Android by ~8 ΔE — do NOT use it. The bare `--color-on-error-container`
 * token does not exist in tokens.css; only `--color-m3-on-error-container` does.
 *
 * @SpecRef PHOTOSTRIP-6.1
 * @SpecRef PHOTOSTRIP-6.2
 */
import { useState } from 'react';
import { useIntl } from 'react-intl';
import { Loader2, AlertCircle, CloudUpload } from 'lucide-react';
import type { PhotoTileState } from './photoTileState';

interface PhotoTileProps {
  state: PhotoTileState;
   
  thumbUrlBuilder: (photoId: number) => string;
   
  onLandedClick?: (photoId: number) => void;
  testIdPrefix?: string;
  /**
   * D-11 + Blocker #1 — already-formatted timestamp string for the {date}
   * interpolation in HIST_PHOTO_BROKEN_TOOLTIP_SERVER_CONTRACT and
   * HIST_PHOTO_UPLOADING_TOOLTIP. Container is responsible for constructing
   * the formatter (see Warning #8 tier note in plan); tile just threads it
   * into formatMessage. Falls back to empty string when undefined.
   */
  formattedDate?: string;
}

const BASE_TILE =
  'shrink-0 w-20 h-20 rounded-md overflow-hidden bg-surface-variant relative ' +
  'transition-shadow duration-200 focus:outline-none focus:ring-2 focus:ring-primary';

const PILL_BASE =
  'absolute size-[22px] rounded-full flex items-center justify-center pointer-events-none';

export function PhotoTile({
  state,
  thumbUrlBuilder,
  onLandedClick,
  testIdPrefix = 'photo-tile',
  formattedDate,
}: PhotoTileProps) {
  const intl = useIntl();
  // R5-002/D9 (audit 2026-07) — a landed photo whose thumb 404s (file
  // missing after a DB-only restore, partial disk loss, manual cleanup)
  // previously rendered a bare browser broken-image glyph inside a working
  // lightbox button, with no indication anything was wrong at the server.
  // The <img> onError flips the tile to the broken treatment instead.
  const [thumbFailed, setThumbFailed] = useState(false);

  if (state.kind === 'landed' && thumbFailed) {
    return (
      <div
        data-testid={`${testIdPrefix}-broken-${state.photoId}`}
        className={`${BASE_TILE} cursor-default shadow-sm bg-m3-error-container`}
        aria-disabled="true"
        title={intl.formatMessage({ id: 'HIST_PHOTO_BROKEN' })}
      >
        <span className={`${PILL_BASE} bottom-1 end-1 bg-m3-error-container`}>
          <AlertCircle size={14} className="text-m3-on-error-container" aria-hidden="true" />
        </span>
      </div>
    );
  }

  if (state.kind === 'landed') {
    return (
      <button
        type="button"
        data-testid={`${testIdPrefix}-landed-${state.photoId}`}
        onClick={() => onLandedClick?.(state.photoId)}
        className={`${BASE_TILE} cursor-zoom-in shadow-sm`}
      >
        <img
          src={thumbUrlBuilder(state.photoId)}
          alt={intl.formatMessage({ id: 'HIST_PHOTO_ALT' }, { id: state.photoId })}
          className="w-full h-full object-cover"
          loading="lazy"
          onError={() => setThumbFailed(true)}
        />
      </button>
    );
  }

  if (state.kind === 'uploading') {
    return (
      <div
        data-testid={`${testIdPrefix}-uploading-${state.photoId}`}
        className={`${BASE_TILE} cursor-default shadow-sm`}
        aria-disabled="true"
        title={intl.formatMessage(
          { id: 'HIST_PHOTO_UPLOADING_TOOLTIP' },
          // D-11 + Blocker #1 — container passes formattedDate prop; empty-string
          // fall-through is spec-correct when uploaded_at is null (uploading kind
          // on PM side has no uploaded_at yet).
          { date: formattedDate ?? '' },
        )}
      >
        <span className={`${PILL_BASE} top-1 end-1 bg-surface-raised/95`}>
          <Loader2 size={14} className="animate-spin text-on-surface-variant" aria-hidden="true" />
        </span>
      </div>
    );
  }

  if (state.kind === 'broken') {
    const tooltipId =
      state.reason.code === 'SERVER_CONTRACT_VIOLATION'
        ? 'HIST_PHOTO_BROKEN_TOOLTIP_SERVER_CONTRACT'
        : 'HIST_PHOTO_BROKEN';
    // Blocker #4 lock — M3-prefixed tokens are the cross-platform parity slot.
    // bg-m3-error-container = #FFDAD6 matches Android Theme.kt:35 errorContainer
    // = BrandTokens.colorM3ErrorContainer. text-m3-on-error-container = #410002
    // matches Android onErrorContainer. The bare bg-error-container token resolves
    // to brand soft pink #fff1f2 which differs from Android by ~8 ΔE — do not use.
    // The bare --color-on-error-container token does not exist in tokens.css —
    // only --color-m3-on-error-container does. (See D-08 cross-platform consistency.)
    return (
      <div
        data-testid={`${testIdPrefix}-broken-${state.photoId}`}
        className={`${BASE_TILE} cursor-default shadow-sm bg-m3-error-container`}
        aria-disabled="true"
        // D-11 + Blocker #1 — container passes formattedDate prop. The {date}
        // interpolation is mandatory per CONTEXT.md verbatim; empty-string
        // fall-through preserves render shape if uploaded_at is somehow null.
        title={intl.formatMessage({ id: tooltipId }, { date: formattedDate ?? '' })}
      >
        <span className={`${PILL_BASE} bottom-1 end-1 bg-m3-error-container`}>
          <AlertCircle size={14} className="text-m3-on-error-container" aria-hidden="true" />
        </span>
      </div>
    );
  }

  if (state.kind === 'local-only') {
    return (
      <div
        data-testid={`${testIdPrefix}-local-only-${state.clientPhotoId}`}
        className={`${BASE_TILE} shadow-sm`}
      >
        <img
          src={state.localUri}
          alt=""
          className="w-full h-full object-cover"
        />
        <span className={`${PILL_BASE} top-1 end-1 bg-surface-raised/95`}>
          <CloudUpload size={14} className="text-on-surface-variant" aria-hidden="true" />
        </span>
      </div>
    );
  }

  // Exhaustiveness — TS narrows to never if all 4 kinds handled.
  const _exhaustive: never = state;
  return _exhaustive;
}
